import { useEffect, useState } from "react";
import { devError } from "@/lib/log";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { VendeIALogo } from "@/components/Brand";
import {
  SquaresFour,
  Package,
  Camera,
  MegaphoneSimple,
  Rocket,
  ArrowUpRight,
  Receipt,
  ShareNetwork,
  EnvelopeSimple,
  ChartBar,
  SignOut,
  Compass,
  Gear,
  Sparkle,
  ClockClockwise,
  ChatCircleDots,
  UsersThree,
  List as ListIcon,
  X,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV_BASE = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, testid: "nav-dashboard", help: "Vista general de KPIs, ventas recientes y acciones rápidas." },
  { to: "/guia", label: "Guía & Flujo", icon: Compass, testid: "nav-guia", help: "Cómo funciona VendeIA: flujo de 7 pasos, ventajas y atajos. Incluye tour guiado." },
  { to: "/inventario", label: "Inventario", icon: Package, testid: "nav-inventario", help: "Catálogo de productos: agregar, editar, ver stock, costo y utilidad." },
  { to: "/escanear", label: "Escanear con AI", icon: Camera, testid: "nav-escanear", help: "Toma fotos del objeto: la IA lo identifica y te sugiere precios nuevo / usado / oportunidad." },
  { to: "/publicaciones", label: "Publicaciones", icon: MegaphoneSimple, testid: "nav-publicaciones", help: "Genera posts para redes sociales con copy + hashtags y arma plan de lanzamiento." },
  { to: "/canales", label: "Canales", icon: MegaphoneSimple, testid: "nav-canales", help: "Directorio de grupos, foros y plataformas donde publicas manualmente. Las métricas te dicen cuáles convierten mejor." },
  { to: "/bandeja", label: "Bandeja auto", icon: Rocket, testid: "nav-bandeja", help: "Borradores generados automáticamente listos para mandar a Instagram, Facebook y WhatsApp en 1 click." },
  { to: "/conversaciones", label: "Conversaciones", icon: ChatCircleDots, testid: "nav-conversaciones", help: "Charlas IA con tus interesados + seguimiento automático con descuentos progresivos." },
  { to: "/crm", label: "CRM clientes", icon: UsersThree, testid: "nav-crm", help: "Compradores fieles agrupados por segmento (VIP, recurrentes, en riesgo). La IA te ayuda a reactivar a los que se enfrían." },
  { to: "/ventas", label: "Ventas", icon: Receipt, testid: "nav-ventas", help: "Lista de pedidos: marca como pagados, enviados o cancelados." },
  { to: "/referidos", label: "Referidos", icon: ShareNetwork, testid: "nav-referidos", help: "Códigos de descuento por referido y métricas de uso." },
  { to: "/suscriptores", label: "Suscriptores", icon: EnvelopeSimple, testid: "nav-suscriptores", help: "Emails capturados desde las páginas públicas de producto." },
  { to: "/reportes", label: "Reportes", icon: ChartBar, testid: "nav-reportes", help: "Corte del día, gráficas semanal/mensual/anual y exportar/importar Excel." },
];

const NAV_ADMIN = [
  { to: "/setup", label: "Instalación", icon: Sparkle, testid: "nav-setup", help: "Wizard que muestra todas las variables con su estatus en colores. Editas con un click y puedes resetear la app para arrancar en cero." },
  { to: "/ajustes", label: "Ajustes", icon: Gear, testid: "nav-ajustes", help: "Datos del negocio, sucursales, usuarios y privilegios por rol — todo en un mismo lugar." },
  { to: "/auditoria", label: "Auditoría", icon: ClockClockwise, testid: "nav-auditoria", help: "Bitácora de todo lo que ocurre en el sistema: altas, ediciones, eliminaciones y accesos." },
];

export default function Layout() {
  const { user, logout, sucursalId, setSucursalId } = useAuth();
  const [sucursales, setSucursales] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    api
      .get("/sucursales")
      .then((r) => setSucursales(r.data || []))
      .catch((e) => devError("load sucursales error", e));
  }, [user]);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Badge visibility is handled globally by <EmergentBadgeGate /> mounted in
  // App.js so it works on every route (including the public product page).
  // We only need to forward the local "vendeia:badge-pref-changed" custom
  // event into a storage-style event so the global gate re-runs without
  // having to share state.
  useEffect(() => {
    const refire = () => window.dispatchEvent(new Event("storage"));
    window.addEventListener("vendeia:badge-pref-changed", refire);
    return () => window.removeEventListener("vendeia:badge-pref-changed", refire);
  }, []);

  const isAdmin = user?.role === "admin";
  const NAV = isAdmin ? [...NAV_BASE, ...NAV_ADMIN] : NAV_BASE;

  const sidebarBody = (
    <>
      <Link to="/" className="px-6 py-6 border-b border-zinc-200 block" data-testid="brand-link">
        <div className="overline text-klein">Plataforma</div>
        <div className="mt-1.5 flex items-center gap-2.5">
          <VendeIALogo size={36} withText={false} />
          <span className="h-display text-3xl font-black tracking-tighter leading-none">
            Vende<span className="text-klein">IA</span>
          </span>
        </div>
        <div className="text-xs text-muted2 mt-2">Inventario · AI Vision · Posts</div>
      </Link>

      {user && (
        <div className="px-6 py-3 border-b border-zinc-200 text-xs" data-testid="layout-user-info">
          <div className="text-muted2">Sesión activa</div>
          <div className="font-medium truncate">{user.name || user.email}</div>
          <div className="text-muted2">{user.role}</div>
        </div>
      )}

      {sucursales.length > 0 && (
        <div className="px-6 py-3 border-b border-zinc-200">
          <label className="overline block mb-1">Sucursal activa</label>
          {isAdmin ? (
            <select
              value={sucursalId}
              onChange={(e) => setSucursalId(e.target.value)}
              data-testid="layout-sucursal-select"
              data-help="Filtra todos los datos del panel por una sucursal específica."
              className="w-full border border-zinc-300 px-2 py-1.5 text-xs bg-white"
            >
              <option value="">— todas —</option>
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          ) : (
            <div className="text-xs font-medium text-ink" data-testid="layout-sucursal-locked">
              {sucursales.find((s) => s.id === user?.sucursal_id)?.nombre || "Sin asignar"}
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            data-help={item.help}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors duration-150 ${
                isActive
                  ? "border-klein bg-zinc-50 text-ink font-medium"
                  : "border-transparent text-muted2 hover:bg-zinc-50 hover:text-ink"
              }`
            }
          >
            <item.icon size={18} weight="regular" />
            <span>{item.label}</span>
            <ArrowUpRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
          </NavLink>
        ))}
      </nav>

      <button
        onClick={logout}
        data-testid="layout-logout-btn"
        data-help="Cierra tu sesión actual y te lleva a la pantalla de ingreso."
        className="border-t border-zinc-200 px-6 py-3 text-sm text-muted2 hover:text-ink hover:bg-zinc-50 flex items-center gap-2 text-left"
      >
        <SignOut size={16} /> Cerrar sesión
      </button>

      <div className="p-6 border-t border-zinc-200 text-xs text-muted2">
        <div className="overline mb-2">Powered by</div>
        <div className="font-mono">GPT-5.2 Vision</div>
        <div className="mt-1">Análisis · Precios · Copy</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white text-ink flex">
      {/* Desktop / iPad landscape sidebar (≥ lg) */}
      <aside className="hidden lg:flex lg:w-72 shrink-0 flex-col border-r border-zinc-200 bg-white sticky top-0 h-screen">
        {sidebarBody}
      </aside>

      {/* Mobile / iPad portrait top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-zinc-200">
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="m-menu-open"
            data-help="Abre el menú lateral con todas las secciones."
            className="p-2 -ml-2 text-ink hover:bg-zinc-50"
            aria-label="Abrir menú"
          >
            <ListIcon size={22} />
          </button>
          <Link to="/" className="inline-flex items-center gap-2">
            <VendeIALogo size={24} withText={false} />
            <span className="h-display font-black text-xl tracking-tighter">
              Vende<span className="text-klein">IA</span>
            </span>
          </Link>
          {user && (
            <div className="text-right text-[11px] leading-tight max-w-[40%] truncate">
              <div className="text-muted2">Hola</div>
              <div className="font-medium truncate">{user.name || user.email}</div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile drawer (slide from left) */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative w-[85%] max-w-[320px] h-full bg-white flex flex-col border-r border-zinc-200 shadow-2xl animate-slide-in">
            <button
              onClick={() => setDrawerOpen(false)}
              data-testid="m-menu-close"
              className="absolute top-3 right-3 p-2 text-muted2 hover:text-ink hover:bg-zinc-50 z-10"
              aria-label="Cerrar menú"
            >
              <X size={22} />
            </button>
            {sidebarBody}
          </aside>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 min-w-0 pt-[60px] lg:pt-0">
        <Outlet />
      </main>

      <Toaster richColors position="top-right" />

      <style>{`
        @keyframes slide-in-x {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in { animation: slide-in-x 220ms cubic-bezier(0.22, 0.61, 0.36, 1); }
      `}</style>
    </div>
  );
}
